import { useTranslation } from '@hbs-us/ui';

import type { MoneyFormatters } from '../../_money/useMoneyFormatters.js';
import type { EstimateStatus } from '../contractsMirror.js';
import { EstimateStatusBadge } from './EstimateStatusBadge.js';

/**
 * The 4-card stat grid on the estimate detail (legacy `EstimateDetailPage`
 * stat-grid): **Status** (badge) · **Base Cost** · **Subtotal** (`--chart-4`
 * amber) · **Markup Value** (`--chart-3` green). Base cost = subtotal − markup;
 * markup value = subtotal − base cost (the legacy `grossMarkupValue`). CSS grid +
 * region semantics (NOT a layout table — A11Y-01); numerals in IBM Plex Mono.
 * Every color is a locked token (UI-SPEC Color "Money-emphasis tokens").
 */
export function EstimateStatGrid({
  status,
  baseCost,
  subtotal,
  markupValue,
  archived,
  fmt,
}: {
  status: EstimateStatus;
  baseCost: number;
  subtotal: number;
  markupValue: number;
  archived: boolean;
  fmt: MoneyFormatters;
}) {
  const { t } = useTranslation();

  return (
    <section
      aria-label={t('estimates:stats.subtotal')}
      className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
    >
      <div className="rounded-md border border-border bg-card p-6">
        <div className="font-semibold text-muted-foreground text-xs uppercase tracking-wide">
          {t('estimates:stats.status')}
        </div>
        <div className="mt-3">
          <EstimateStatusBadge status={status} archived={archived} />
        </div>
      </div>
      <div className="rounded-md border border-border bg-card p-6">
        <div className="font-semibold text-muted-foreground text-xs uppercase tracking-wide">
          {t('estimates:stats.baseCost')}
        </div>
        <div className="mt-2 font-mono font-semibold text-foreground text-xl">
          {fmt.formatCurrency(baseCost)}
        </div>
      </div>
      <div className="rounded-md border border-border bg-card p-6">
        <div className="font-semibold text-muted-foreground text-xs uppercase tracking-wide">
          {t('estimates:stats.subtotal')}
        </div>
        <div className="mt-2 font-mono font-semibold text-warning text-xl">
          {fmt.formatCurrency(subtotal)}
        </div>
      </div>
      <div className="rounded-md border border-border bg-card p-6">
        <div className="font-semibold text-muted-foreground text-xs uppercase tracking-wide">
          {t('estimates:stats.markupValue')}
        </div>
        <div className="mt-2 font-mono font-semibold text-success text-xl">
          {fmt.formatCurrency(markupValue)}
        </div>
      </div>
    </section>
  );
}
